import { useNavigate } from 'react-router-dom';
import { Community } from '@/mocks/communities';

interface RelatedCommunitiesProps {
  current: Community;
  all: Community[];
}

const activityConfig = {
  high: { dot: 'bg-emerald-500', color: 'text-emerald-600' },
  medium: { dot: 'bg-amber-500', color: 'text-amber-600' },
  low: { dot: 'bg-slate-400', color: 'text-slate-400' },
};

export default function RelatedCommunities({ current, all }: RelatedCommunitiesProps) {
  const navigate = useNavigate();

  const related = all
    .filter((c) => c.id !== current.id && (c.category === current.category || c.region === current.region))
    .sort((a, b) => b.memberCount - a.memberCount)
    .slice(0, 6);

  const formatCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toString());

  if (related.length === 0) return null;

  return (
    <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 p-6">
      <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
        <span className="w-6 h-6 flex items-center justify-center text-[#0088CC]">
          <i className="ri-group-2-line text-base"></i>
        </span>
        Similar Communities
      </h2>
      <p className="text-xs text-slate-400 mb-5 ml-8">
        More communities in {current.category} &amp; {current.region}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {related.map((c) => {
          const activity = activityConfig[c.activityLevel];
          return (
            <button
              key={c.id}
              onClick={() => navigate(`/community/${c.id}`)}
              className="group flex items-center gap-3 bg-white/60 hover:bg-white/90 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/50 hover:border-[#0088CC]/30 p-3 transition-all duration-200 cursor-pointer text-left"
            >
              {/* Avatar */}
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-white/80">
                <img
                  src={c.avatar}
                  alt={c.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 group-hover:text-[#0088CC] transition-colors truncate leading-snug">
                  {c.name}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-1">
                    <i className="ri-group-line text-[10px] text-slate-400 w-3 h-3 flex items-center justify-center"></i>
                    <span className="text-[11px] text-slate-400">{formatCount(c.memberCount)}</span>
                  </div>
                  <span className="text-slate-300">·</span>
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${activity.dot}`}></span>
                    <span className={`text-[10px] ${activity.color}`}>{c.activityLevel}</span>
                  </div>
                </div>
              </div>
              <div className="w-5 h-5 flex items-center justify-center text-slate-300 group-hover:text-[#0088CC] transition-colors shrink-0">
                <i className="ri-arrow-right-s-line text-base"></i>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
